import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiCreditCard, FiFileText, FiUsers, FiBook } from 'react-icons/fi';
import Card from '../components/ui/Card';
import Avatar from '../components/ui/Avatar';
import { useAuth } from '../contexts/AuthContext';
import { CHARACTERS } from '../assets/characters';

interface QuickLink {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  route: string;
}

const QUICK_LINKS: QuickLink[] = [
  {
    id: 'absences',
    label: 'Absences',
    description: 'Poser une demande et consulter vos soldes',
    icon: <FiCalendar size={22} />,
    color: '#0EA5E9',
    route: '/absences',
  },
  {
    id: 'temps',
    label: 'Feuilles de temps',
    description: 'Saisir et suivre vos heures',
    icon: <FiClock size={22} />,
    color: '#0284C7',
    route: '/mes-temps',
  },
  {
    id: 'frais',
    label: 'Notes de frais',
    description: 'Déclarer vos dépenses professionnelles',
    icon: <FiFileText size={22} />,
    color: '#14B8A6',
    route: '/notes-de-frais',
  },
  {
    id: 'annuaire',
    label: 'Annuaire',
    description: 'Retrouver les contacts de vos collègues',
    icon: <FiBook size={22} />,
    color: '#06B6D4',
    route: '/annuaire',
  },
  {
    id: 'organigramme',
    label: 'Organigramme',
    description: 'Consulter la structure de l\'entreprise',
    icon: <FiUsers size={22} />,
    color: '#0EA5E9',
    route: '/organigramme',
  },
  {
    id: 'paie',
    label: 'Bulletins de paie',
    description: 'Consulter et télécharger vos bulletins',
    icon: <FiCreditCard size={22} />,
    color: '#0891B2',
    route: '/ma-paie',
  },
];

const ACTUALITES = [
  {
    id: 'a1',
    titre: 'Réforme de l\'assurance chômage',
    date: '10 juin 2026',
    description: 'Nouvelles règles d\'indemnisation applicables au 1er juillet.',
  },
  {
    id: 'a2',
    titre: 'Revalorisation du SMIC',
    date: '8 juin 2026',
    description: 'Nouvelle grille applicable dès le 1er juillet.',
  },
];

const CollaborateurDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firstName = user?.first_name || 'Collaborateur';

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, #ECFEFF 0%, #E0F2FE 50%, #F0FDFA 100%)',
          borderRadius: 'var(--radius-2xl)',
          padding: '2rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Avatar
            name={user ? `${user.first_name} ${user.last_name}` : ''}
            size={56}
            src={user?.avatar_url}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
              Bonjour {firstName}
            </h1>
            <p style={{ margin: '0.25rem 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              Votre espace personnel Jul&apos;IA
            </p>
          </div>
        </div>
        <img
          src={CHARACTERS.collaborateur}
          alt=""
          aria-hidden
          style={{ width: 108, height: 'auto', flexShrink: 0 }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => navigate('/annuaire')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          <FiBook size={16} />
          Annuaire
        </button>
        <button
          type="button"
          onClick={() => navigate('/organigramme')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          <FiUsers size={16} />
          Consulter l&apos;organigramme
        </button>
        </div>
      </div>

      <p style={{ margin: '0 0 1rem', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
        Accès rapides
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        {QUICK_LINKS.map((link) => (
          <Card
            key={link.id}
            hover
            padding="1.25rem"
            onClick={() => navigate(link.route)}
            style={{ cursor: 'pointer' }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 'var(--radius-lg)',
                background: `${link.color}18`,
                color: link.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '0.75rem',
              }}
            >
              {link.icon}
            </div>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{link.label}</p>
            <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              {link.description}
            </p>
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <Card padding="1.5rem">
          <h2 style={{ margin: '0 0 1rem', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
            Dernières actualités
          </h2>
          {ACTUALITES.map((actu) => (
            <div key={actu.id} style={{ marginBottom: '1rem' }}>
              <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{actu.titre}</p>
              <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{actu.date}</p>
              <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{actu.description}</p>
            </div>
          ))}
        </Card>
        <Card padding="1.5rem" style={{ background: 'var(--color-brand-light)' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-brand-dark)' }}>
            Besoin d&apos;aide ?
          </h2>
          <p style={{ margin: '0 0 1rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            Julia peut répondre à vos questions sur vos droits, vos documents ou vos démarches RH.
            Votre service RH reste disponible pour valider vos demandes.
          </p>
          <img src="/character-julia.svg" alt="Julia" style={{ maxWidth: 140, margin: '0 auto', display: 'block' }} />
        </Card>
      </div>
    </div>
  );
};

export default CollaborateurDashboardPage;
